"use client";

import { useReducedMotion } from "motion/react";
import Image from "next/image";

import { buildTrackerProps } from "@trex-2b/libs";

export type FiveDimensionId =
  | "demographic"
  | "assets"
  | "influence"
  | "interests"
  | "knowledge";

export type FiveDimensionAccordionItem = {
  id: FiveDimensionId;
  title: string;
  subtitle: string;
  description: string;
  rows: [string, string][];
};

const BORDER_MUTED = "border border-solid border-[rgba(28,25,22,0.6)]";

const ACCORDION_EASE = "cubic-bezier(0.33, 1, 0.68, 1)";
const ACCORDION_DURATION_MS = 320;

/** Chevron — Figma `30535:2208` / list `30535:2231`; 32×32 in design. */
function CaretCircleUp({
  pointDown,
  className,
}: {
  pointDown?: boolean;
  className?: string;
}) {
  return (
    <div
      className={`relative size-6 shrink-0 transition-transform duration-320 ease-out motion-reduce:transition-none ${pointDown ? "rotate-180" : "rotate-0"} ${className ?? ""}`}
    >
      <Image
        alt=""
        className="block size-full max-w-none"
        src={"/images/landing/landing-arrow.svg"}
        width={24}
        height={24}
      />
    </div>
  );
}

/** Header row — same `gap-4` + flex split as Figma `30535:2231` / `30535:2241` (caret `shrink-0`). */
function DimensionAccordionHeaderRow({
  title,
  subtitle,
  collapsedTypography,
  pointDown,
}: {
  title: string;
  subtitle: string;
  collapsedTypography: boolean;
  pointDown: boolean;
}) {
  return (
    <div className="flex w-full items-center gap-4">
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <span
          className={`shrink-0 text-black tracking-[0.14px] transition-[font-size,font-weight,line-height,letter-spacing,color] duration-320 ease-out motion-reduce:transition-none ${
            collapsedTypography
              ? "text-lg font-semibold leading-5"
              : "text-xl font-extrabold leading-8 tracking-[-0.3px] text-[#31373d]"
          }`}
        >
          {title}
        </span>
        <span className="min-w-0 truncate text-sm leading-normal font-normal text-[#4a4a4a]">
          {subtitle}
        </span>
      </div>
      <CaretCircleUp pointDown={pointDown} />
    </div>
  );
}

function KeyValueRows({ rows }: { rows: [string, string][] }) {
  return (
    <div className="flex w-full flex-col divide-y divide-solid divide-[#e8e8e8] font-inter">
      {rows.map(([label, value]) => (
        <div
          key={`${label}-${value}`}
          className="flex w-full items-start justify-between gap-4 py-3 text-sm tracking-[0.14px]"
        >
          <span className="max-w-[55%] font-normal leading-5 text-black">
            {label}
          </span>
          <span className="shrink-0 text-right font-bold leading-5 whitespace-nowrap text-black">
            {value}
          </span>
        </div>
      ))}
    </div>
  );
}

type Props = {
  item: FiveDimensionAccordionItem;
  expanded: boolean;
  onToggleExpanded: () => void;
};

/**
 * Five-dimension accordion row — collapsed `30535:2233`; expanded gradient `30535:2239`.
 * Horizontal inset `18px` in both states so chevron right margin matches design + product request.
 *
 * Single DOM tree + grid 0fr/1fr: height folds with sibling cards when `expandedId` swaps in one update.
 */
export function LandingDimensionAccordion({
  item,
  expanded,
  onToggleExpanded,
}: Props) {
  const reduceMotion = useReducedMotion();

  return (
    <div className="w-full shrink-0" role="listitem">
      <div
        className={`overflow-hidden rounded-[13px] border border-solid transition-[border-color] duration-320 ease-out motion-reduce:transition-none ${
          expanded
            ? "border-[#1c1916] bg-linear-to-b from-[rgba(153,215,213,0.2)] to-[rgba(184,184,255,0.2)]"
            : `bg-white ${BORDER_MUTED}`
        }`}
      >
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          data-testid={`landing-dimension-accordion-${item.id}-toggle-btn`}
          {...buildTrackerProps({
            params: {
              element_id: `landing-dimension-accordion-${item.id}-toggle-btn`,
            },
          })}
          className={`flex w-full cursor-pointer flex-col justify-center bg-transparent px-[18px] text-left outline-none transition-[padding] duration-320 ease-out motion-reduce:transition-none ${
            expanded ? "pt-[18px] pb-3" : "h-[54px] py-0"
          }`}
        >
          <DimensionAccordionHeaderRow
            title={item.title}
            subtitle={item.subtitle}
            collapsedTypography={!expanded}
            pointDown={!expanded}
          />
        </button>

        <div
          className="grid"
          style={{
            gridTemplateRows: expanded ? "1fr" : "0fr",
            transition: reduceMotion
              ? "none"
              : `grid-template-rows ${ACCORDION_DURATION_MS}ms ${ACCORDION_EASE}`,
          }}
        >
          <div className="min-h-0 overflow-hidden">
            <div
              className={`flex flex-col gap-6 px-[18px] font-inter transition-opacity duration-280 ease-out motion-reduce:transition-none ${
                expanded
                  ? "pb-[18px] opacity-100"
                  : "pointer-events-none pb-0 opacity-0"
              }`}
            >
              <p className="text-sm leading-normal font-normal text-[#4a4a4a]">
                {item.description}
              </p>
              <KeyValueRows rows={item.rows} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
